import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";
import { resolvePromotion } from "../config/promotions";

const STORAGE_KEY = "commerceState.v1";
const LEGACY_CART_STORAGE_KEY = "cartItems";
const MAX_CART_QUANTITY = 99;
const MAX_RECENTLY_VIEWED = 12;

const DEFAULT_STATE = {
  cartItems: [],
  wishlistIds: [],
  user: null,
  coupon: null,
  deliveryMethod: "standard",
  recentlyViewed: [],
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getProductId = (productOrId) => {
  if (isPlainObject(productOrId)) {
    return productOrId.productId ?? productOrId.id;
  }

  return productOrId;
};

const hasValue = (value) => value !== undefined && value !== null;

const comparableValue = (value) => (hasValue(value) ? String(value) : "");

const scalarValue = (value) =>
  Array.isArray(value) || isPlainObject(value) ? undefined : value;

const isQuantityValue = (value) =>
  (typeof value === "number" && Number.isFinite(value)) ||
  (typeof value === "string" &&
    value.trim() !== "" &&
    Number.isFinite(Number(value)));

const clampQuantity = (quantity) => {
  const parsedQuantity = Number(quantity);

  if (!Number.isFinite(parsedQuantity)) return 1;

  return Math.min(
    MAX_CART_QUANTITY,
    Math.max(1, Math.trunc(parsedQuantity))
  );
};

const getVariantKey = (item) => {
  if (hasValue(item.variantId)) {
    return `variant:${comparableValue(item.variantId)}`;
  }

  return [
    "legacy",
    comparableValue(item.size),
    comparableValue(item.color),
    comparableValue(item.variantLabel),
  ].join(":");
};

const isSameCartItem = (firstItem, secondItem) =>
  comparableValue(getProductId(firstItem)) ===
    comparableValue(getProductId(secondItem)) &&
  getVariantKey(firstItem) === getVariantKey(secondItem);

const normalizeCartItem = (item) => {
  if (!isPlainObject(item)) return null;

  const productId = getProductId(item);
  if (!hasValue(productId) || comparableValue(productId) === "") return null;

  return {
    ...item,
    id: item.id ?? productId,
    productId,
    quantity: clampQuantity(item.quantity),
  };
};

const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];

  return items.reduce((normalizedItems, candidate) => {
    const item = normalizeCartItem(candidate);
    if (!item) return normalizedItems;

    const existingIndex = normalizedItems.findIndex((existingItem) =>
      isSameCartItem(existingItem, item)
    );

    if (existingIndex === -1) return [...normalizedItems, item];

    return normalizedItems.map((existingItem, index) =>
      index === existingIndex
        ? {
            ...existingItem,
            ...item,
            id: existingItem.id ?? item.id,
            quantity: clampQuantity(existingItem.quantity + item.quantity),
          }
        : existingItem
    );
  }, []);
};

const resolveCatalogCartItem = (item) => {
  const product = productData.find(
    (candidate) => comparableValue(candidate.id) === comparableValue(getProductId(item))
  );
  if (!product) return null;

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variant = variants.find((candidate) =>
    comparableValue(candidate.id) === comparableValue(item.variantId) ||
    comparableValue(candidate.label) === comparableValue(item.variantLabel) ||
    comparableValue(candidate.label) === comparableValue(item.size) ||
    comparableValue(candidate.weight) === comparableValue(item.size)
  ) || variants[0];

  return {
    ...item,
    id: product.id,
    productId: product.id,
    variantId: variant?.id,
    variantLabel: variant?.label,
    unitPrice: Number(variant?.price ?? product.price) || 0,
  };
};

const hydrateCartItems = (items) => normalizeCartItems(
  normalizeCartItems(items).map(resolveCatalogCartItem).filter(Boolean)
);

const normalizeIdList = (values) => {
  if (!Array.isArray(values)) return [];

  return values.reduce((ids, value) => {
    const id = getProductId(value);
    if (!hasValue(id) || comparableValue(id) === "") return ids;

    const isDuplicate = ids.some(
      (existingId) => comparableValue(existingId) === comparableValue(id)
    );

    return isDuplicate ? ids : [...ids, id];
  }, []);
};

const getRecentlyViewedKey = (item) => comparableValue(getProductId(item));

const normalizeRecentlyViewed = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .reduce((recentItems, item) => {
      const key = getRecentlyViewedKey(item);
      if (!key || recentItems.some((entry) => getRecentlyViewedKey(entry) === key)) {
        return recentItems;
      }

      return [...recentItems, item];
    }, [])
    .slice(0, MAX_RECENTLY_VIEWED);
};

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch (_error) {
    return null;
  }
};

const readStorage = (key) => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (_error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Storage may be disabled, full, or unavailable in a test/SSR environment.
  }
};

const createInitialState = () => {
  const storedState = readStorage(STORAGE_KEY);
  const validStoredState = isPlainObject(storedState) ? storedState : {};
  const legacyCartItems = readStorage(LEGACY_CART_STORAGE_KEY);
  const cartItems = Array.isArray(validStoredState.cartItems)
    ? validStoredState.cartItems
    : legacyCartItems;

  return {
    ...DEFAULT_STATE,
    ...validStoredState,
    cartItems: hydrateCartItems(cartItems),
    wishlistIds: normalizeIdList(validStoredState.wishlistIds),
    user: isPlainObject(validStoredState.user) ? validStoredState.user : null,
    coupon: resolvePromotion(validStoredState.coupon),
    deliveryMethod:
      validStoredState.deliveryMethod ?? DEFAULT_STATE.deliveryMethod,
    recentlyViewed: normalizeRecentlyViewed(validStoredState.recentlyViewed),
  };
};

const sanitizeUser = (userDetails) => {
  const details =
    typeof userDetails === "string" ? { email: userDetails } : userDetails;

  if (!isPlainObject(details)) return {};

  const {
    password: _password,
    passwordConfirmation: _passwordConfirmation,
    confirmPassword: _confirmPassword,
    ...safeDetails
  } = details;

  return safeDetails;
};

const createCartItem = (
  productOrId,
  quantityOrOptions = 1,
  sizeOrOptions,
  color
) => {
  const product = isPlainObject(productOrId) ? productOrId : {};
  const productId = getProductId(productOrId);
  const quantityOptions = isPlainObject(quantityOrOptions)
    ? quantityOrOptions
    : {};
  const variantOptions = isPlainObject(sizeOrOptions) ? sizeOrOptions : {};
  const isLegacyVariant =
    !isPlainObject(sizeOrOptions) &&
    (hasValue(sizeOrOptions) || hasValue(color));
  const options = { ...quantityOptions, ...variantOptions };
  const quantity = isPlainObject(quantityOrOptions)
    ? quantityOrOptions.quantity
    : quantityOrOptions;

  return normalizeCartItem({
    ...product,
    ...options,
    id: product.id ?? productId,
    productId,
    quantity: quantity ?? options.quantity ?? 1,
    size: isLegacyVariant
      ? sizeOrOptions
      : scalarValue(options.size ?? product.size),
    color: isLegacyVariant
      ? color
      : scalarValue(options.color ?? product.color),
    variantId: options.variantId ?? product.variantId,
    variantLabel: options.variantLabel ?? product.variantLabel,
  });
};

const createCartSelector = (
  productOrItem,
  sizeOrVariant,
  color,
  flexibleVariantToken = false
) => {
  const item = isPlainObject(productOrItem) ? productOrItem : {};
  const selectorOptions = isPlainObject(sizeOrVariant) ? sizeOrVariant : {};
  const hasLegacyPair =
    !isPlainObject(sizeOrVariant) && hasValue(color);
  const hasSingleToken =
    !isPlainObject(sizeOrVariant) &&
    hasValue(sizeOrVariant) &&
    !hasValue(color);

  return {
    productId: getProductId(productOrItem),
    variantId:
      selectorOptions.variantId ??
      item.variantId ??
      (hasSingleToken ? sizeOrVariant : undefined),
    variantLabel: scalarValue(
      selectorOptions.variantLabel ?? item.variantLabel
    ),
    size:
      scalarValue(selectorOptions.size ?? item.size) ??
      (hasLegacyPair || (hasSingleToken && flexibleVariantToken)
        ? sizeOrVariant
        : undefined),
    color:
      scalarValue(selectorOptions.color ?? item.color) ??
      (hasLegacyPair ? color : undefined),
    flexibleVariantToken: hasSingleToken && flexibleVariantToken,
  };
};

const matchesCartSelector = (item, selector) => {
  if (
    comparableValue(getProductId(item)) !== comparableValue(selector.productId)
  ) {
    return false;
  }

  const hasVariantSelector =
    hasValue(selector.variantId) ||
    hasValue(selector.variantLabel) ||
    hasValue(selector.size) ||
    hasValue(selector.color);

  if (!hasVariantSelector) return true;

  if (selector.flexibleVariantToken) {
    const token = comparableValue(selector.variantId);
    return (
      comparableValue(item.variantId) === token ||
      (!hasValue(item.variantId) && comparableValue(item.size) === token)
    );
  }

  if (
    hasValue(selector.variantId) &&
    comparableValue(item.variantId) !== comparableValue(selector.variantId)
  ) {
    return false;
  }

  if (
    hasValue(selector.variantLabel) &&
    comparableValue(item.variantLabel) !== comparableValue(selector.variantLabel)
  ) {
    return false;
  }

  if (
    hasValue(selector.size) &&
    comparableValue(item.size) !== comparableValue(selector.size)
  ) {
    return false;
  }

  return (
    !hasValue(selector.color) ||
    comparableValue(item.color) === comparableValue(selector.color)
  );
};

const parsePrice = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const match = value.match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!match) return 0;

  const parsedPrice = Number(match[0].replace(/,/g, ""));
  return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

export const CartContext = createContext(undefined);

export const CartProvider = ({ children }) => {
  const [state, setState] = useState(createInitialState);

  const {
    cartItems,
    wishlistIds,
    user,
    coupon,
    deliveryMethod,
    recentlyViewed,
  } = state;

  useEffect(() => {
    writeStorage(STORAGE_KEY, state);
    // Keep the original key in sync so older builds can still read the cart.
    writeStorage(LEGACY_CART_STORAGE_KEY, cartItems);
  }, [cartItems, state]);

  const addToCart = useCallback(
    (productOrId, quantityOrOptions = 1, sizeOrOptions, color) => {
      const cartItem = resolveCatalogCartItem(createCartItem(
        productOrId,
        quantityOrOptions,
        sizeOrOptions,
        color
      ));

      if (!cartItem) return;

      setState((currentState) => {
        const existingIndex = currentState.cartItems.findIndex((item) =>
          isSameCartItem(item, cartItem)
        );

        if (existingIndex === -1) {
          return {
            ...currentState,
            cartItems: [...currentState.cartItems, cartItem],
          };
        }

        return {
          ...currentState,
          cartItems: currentState.cartItems.map((item, index) =>
            index === existingIndex
              ? {
                  ...item,
                  ...cartItem,
                  id: item.id ?? cartItem.id,
                  quantity: clampQuantity(item.quantity + cartItem.quantity),
                }
              : item
          ),
        };
      });
    },
    []
  );

  const removeFromCart = useCallback((productOrItem, sizeOrOptions, color) => {
    const selector = createCartSelector(
      productOrItem,
      sizeOrOptions,
      color,
      true
    );

    if (!hasValue(selector.productId)) return;

    setState((currentState) => ({
      ...currentState,
      cartItems: currentState.cartItems.filter(
        (item) => !matchesCartSelector(item, selector)
      ),
    }));
  }, []);

  const updateQuantity = useCallback(
    (productOrItem, sizeOrQuantity, colorOrQuantity, maybeQuantity) => {
      let selector;
      let quantity;

      if (
        isPlainObject(productOrItem) &&
        isQuantityValue(sizeOrQuantity) &&
        !hasValue(colorOrQuantity) &&
        !hasValue(maybeQuantity)
      ) {
        selector = createCartSelector(productOrItem);
        quantity = sizeOrQuantity;
      } else if (isPlainObject(sizeOrQuantity)) {
        selector = createCartSelector(productOrItem, sizeOrQuantity);
        quantity = colorOrQuantity;
      } else if (hasValue(maybeQuantity)) {
        // Backwards compatible: updateQuantity(id, size, color, quantity)
        selector = createCartSelector(
          productOrItem,
          sizeOrQuantity,
          colorOrQuantity
        );
        quantity = maybeQuantity;
      } else if (isQuantityValue(colorOrQuantity)) {
        // Variant-aware shorthand: updateQuantity(id, variantId, quantity)
        selector = createCartSelector(
          productOrItem,
          sizeOrQuantity,
          undefined,
          true
        );
        quantity = colorOrQuantity;
      } else {
        // Product-only shorthand: updateQuantity(id, quantity)
        selector = createCartSelector(productOrItem);
        quantity = sizeOrQuantity;
      }

      if (!hasValue(selector.productId)) return;

      const nextQuantity = clampQuantity(quantity);
      setState((currentState) => ({
        ...currentState,
        cartItems: currentState.cartItems.map((item) =>
          matchesCartSelector(item, selector)
            ? { ...item, quantity: nextQuantity }
            : item
        ),
      }));
    },
    []
  );

  const clearCart = useCallback(() => {
    setState((currentState) => ({ ...currentState, cartItems: [] }));
  }, []);

  const completeOrder = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      cartItems: [],
      coupon: null,
      deliveryMethod: DEFAULT_STATE.deliveryMethod,
    }));
  }, []);

  const addToWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (!hasValue(productId) || comparableValue(productId) === "") return;

    setState((currentState) => {
      const alreadyExists = currentState.wishlistIds.some(
        (id) => comparableValue(id) === comparableValue(productId)
      );

      return alreadyExists
        ? currentState
        : {
            ...currentState,
            wishlistIds: [...currentState.wishlistIds, productId],
          };
    });
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (!hasValue(productId)) return;

    setState((currentState) => ({
      ...currentState,
      wishlistIds: currentState.wishlistIds.filter(
        (id) => comparableValue(id) !== comparableValue(productId)
      ),
    }));
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (!hasValue(productId) || comparableValue(productId) === "") return;

    setState((currentState) => {
      const alreadyExists = currentState.wishlistIds.some(
        (id) => comparableValue(id) === comparableValue(productId)
      );

      return {
        ...currentState,
        wishlistIds: alreadyExists
          ? currentState.wishlistIds.filter(
              (id) => comparableValue(id) !== comparableValue(productId)
            )
          : [...currentState.wishlistIds, productId],
      };
    });
  }, []);

  const isWishlisted = useCallback(
    (productOrId) => {
      const productId = getProductId(productOrId);
      return wishlistIds.some(
        (id) => comparableValue(id) === comparableValue(productId)
      );
    },
    [wishlistIds]
  );

  const login = useCallback((userDetails) => {
    const safeUser = sanitizeUser(userDetails);
    setState((currentState) => ({
      ...currentState,
      user: { ...safeUser, isAuthenticated: true },
    }));
  }, []);

  const register = useCallback((userDetails) => {
    const safeUser = sanitizeUser(userDetails);
    setState((currentState) => ({
      ...currentState,
      user: {
        ...safeUser,
        isAuthenticated: true,
        registeredAt: safeUser.registeredAt ?? new Date().toISOString(),
      },
    }));
  }, []);

  const logout = useCallback(() => {
    setState((currentState) => ({ ...currentState, user: null }));
  }, []);

  const updateProfile = useCallback((profileUpdates) => {
    const safeUpdates = sanitizeUser(profileUpdates);
    setState((currentState) => ({
      ...currentState,
      user: currentState.user
        ? { ...currentState.user, ...safeUpdates }
        : { ...safeUpdates, isAuthenticated: false },
    }));
  }, []);

  const applyCoupon = useCallback((couponDetails) => {
    const nextCoupon = resolvePromotion(couponDetails);

    setState((currentState) => ({
      ...currentState,
      coupon: nextCoupon,
    }));

    return nextCoupon;
  }, []);

  const removeCoupon = useCallback(() => {
    setState((currentState) => ({ ...currentState, coupon: null }));
  }, []);

  const setDeliveryMethod = useCallback((method) => {
    setState((currentState) => ({
      ...currentState,
      deliveryMethod: method ?? DEFAULT_STATE.deliveryMethod,
    }));
  }, []);

  const addToRecentlyViewed = useCallback((productOrId) => {
    const key = getRecentlyViewedKey(productOrId);
    if (!key) return;

    setState((currentState) => ({
      ...currentState,
      recentlyViewed: [
        productOrId,
        ...currentState.recentlyViewed.filter(
          (item) => getRecentlyViewedKey(item) !== key
        ),
      ].slice(0, MAX_RECENTLY_VIEWED),
    }));
  }, []);

  const clearRecentlyViewed = useCallback(() => {
    setState((currentState) => ({ ...currentState, recentlyViewed: [] }));
  }, []);

  const cartCount = useMemo(
    () => cartItems.reduce((count, item) => count + clampQuantity(item.quantity), 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () =>
      cartItems.reduce((subtotal, item) => {
        const price =
          item.unitPrice ??
          item.salePrice ??
          item.price ??
          item.product?.price ??
          0;
        return subtotal + parsePrice(price) * clampQuantity(item.quantity);
      }, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      completeOrder,
      cartCount,
      cartSubtotal,
      wishlistIds,
      toggleWishlist,
      addToWishlist,
      removeFromWishlist,
      isWishlisted,
      user,
      isAuthenticated: Boolean(user?.isAuthenticated),
      login,
      register,
      logout,
      updateProfile,
      coupon,
      applyCoupon,
      removeCoupon,
      deliveryMethod,
      setDeliveryMethod,
      recentlyViewed,
      addToRecentlyViewed,
      clearRecentlyViewed,
    }),
    [
      addToCart,
      addToRecentlyViewed,
      addToWishlist,
      applyCoupon,
      cartCount,
      cartItems,
      cartSubtotal,
      clearCart,
      completeOrder,
      clearRecentlyViewed,
      coupon,
      deliveryMethod,
      isWishlisted,
      login,
      logout,
      recentlyViewed,
      register,
      removeCoupon,
      removeFromCart,
      removeFromWishlist,
      setDeliveryMethod,
      toggleWishlist,
      updateProfile,
      updateQuantity,
      user,
      wishlistIds,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider.");
  }

  return context;
};
